/**
 * Hilfe: wie die App arbeitet und warum sie es so tut.
 *
 * Für jemanden, der eine Maske vor sich hat und nicht weiterweiß. Deshalb
 * nach Tätigkeiten gegliedert, in der Reihenfolge des Arbeitsablaufs, und zu
 * jeder Regel der Grund. Eine Regel ohne Grund wird umgangen, sobald sie stört.
 *
 * Die Fachlogik steht ausführlicher in docs/ARBEITSABLAUF.md. Ändert sich eine
 * Regel, sind beide mitzupflegen.
 */
import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { PageHeader } from "../components/ui";

interface Thema {
  id: string;
  titel: string;
  /** Zusätzliche Suchbegriffe, die nicht im Titel stehen. */
  stichworte: string;
  inhalt: ReactNode;
}

/** Wie etwas geht. */
function Schritte({ children }: { children: ReactNode }) {
  return <div className="space-y-3 text-sm leading-relaxed">{children}</div>;
}

/** Warum es so ist. Optisch abgesetzt, damit man es auch überspringen kann. */
function Warum({ children }: { children: ReactNode }) {
  return (
    <div className="mt-4 border-l-2 border-accent/60 pl-4 text-sm leading-relaxed text-muted">
      <div className="label mb-1 text-accent">Warum</div>
      {children}
    </div>
  );
}

/** Ein Verweis auf eine Maske, so beschriftet wie in der Navigation. */
function Maske({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="font-medium text-ink underline decoration-line underline-offset-2 hover:decoration-ink">
      {children}
    </Link>
  );
}

function Tabelle({ kopf, zeilen }: { kopf: string[]; zeilen: ReactNode[][] }) {
  return (
    <div className="card my-3 overflow-x-auto">
      <table className="tbl">
        <thead>
          <tr>
            {kopf.map((k) => (
              <th key={k}>{k}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {zeilen.map((z, i) => (
            <tr key={i}>
              {z.map((zelle, j) => (
                <td key={j} className="align-top">
                  {zelle}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const THEMEN: Thema[] = [
  {
    id: "ablauf",
    titel: "Der Ablauf im Überblick",
    stichworte:
      "halbmonat abrechnungszeitraum übersicht nächster schritt reihenfolge neue abrechnung starten nächste hälfte abgeschlossen",
    inhalt: (
      <>
        <Schritte>
          <p>
            Zweimal im Monat läuft dieselbe Kette: Dateien einlesen, Kassen klären, Tage
            rechnen, Tage freigeben, Auszahlung anlegen. Auswendig lernen muss sie niemand. Die{" "}
            <Maske to="/">Übersicht</Maske> zeigt sie oben an und hebt genau einen Schritt
            hervor, den nächsten. Grün ist erledigt, blau ist dran, grau kommt später. Derselbe
            Schritt steht auch im Kopf jeder Seite und führt per Klick dorthin.
          </p>
          <p>
            Abgerechnet wird je Halbmonat: vom 1. bis 15. und vom 16. bis zum Monatsende. Alle
            Masken zeigen den jüngsten Halbmonat, in dem noch etwas offen ist. Anfang Oktober
            kann das also noch die zweite Septemberhälfte sein.
          </p>
          <p>
            Ist eine Hälfte abgeschlossen, also ihre Auszahlung gesperrt, steht oben schon die
            nächste, mit dem Knopf <strong>Neue Abrechnung starten</strong>. Er führt zum Import.
            Darunter steht, dass die alte erledigt ist, und solange ihre Überweisungsdatei fehlt,
            ein Hinweis dazu. Läuft die nächste Hälfte noch, steht dort nur „läuft noch“.
          </p>
          <p>
            Hilfe gibt es von jeder Seite aus: <strong>? Hilfe</strong> oben rechts springt
            direkt zu dem Thema, das zur geöffneten Seite gehört.
          </p>
        </Schritte>
        <Warum>
          Ausgezahlt wird halbmonatlich, also ist der Halbmonat die Einheit, in der etwas
          fertig wird. Ein Kalendermonat würde zwei Auszahlungen vermischen, und die offene
          Hälfte ginge neben der fertigen unter.
        </Warum>
      </>
    ),
  },
  {
    id: "import",
    titel: "Dateien einlesen",
    stichworte: "import ecos vr gastromatic csv xlsx export hochladen datei übernehmen verwerfen doppelt überschneidung korrektur abweichung neuer stand freigegeben",
    inhalt: (
      <>
        <Schritte>
          <p>
            Unter <Maske to="/import">Import</Maske> werden drei Exporte gebraucht:
          </p>
          <Tabelle
            kopf={["Quelle", "Was drinsteht"]}
            zeilen={[
              ["ecos Kassenabschlüsse (CSV)", "was jede Kasse an Kartenumsatz erwartet"],
              ["VR Terminal-Reports (CSV)", "was am Karten-Terminal tatsächlich angekommen ist"],
              ["Gastromatic Stunden (xlsx oder CSV)", "wer wann in welchem Bereich gearbeitet hat"],
            ]}
          />
          <p>
            Alle drei Dateien auf einmal auswählen. Welche welche ist, erkennt die App am
            Inhalt. Danach steht je Datei eine Karte mit Zeitraum, Zeilenzahl und allem, was
            übersprungen wurde, jeweils mit Grund. Erst <strong>Alle übernehmen</strong>{" "}
            schreibt etwas in den Bestand.
          </p>
          <p>
            Beim VR-Export darauf achten, dass er bis 10:00 Uhr am Morgen nach dem letzten
            Abend reicht. Die Terminals des letzten Abends werden oft erst am nächsten Morgen
            ausgelesen; fehlen diese Werte, meldet die App sie später als fehlend.
          </p>
          <p>
            <strong>Zweiter Export für dieselben Tage</strong>, etwa weil der erste zu weit
            reichte oder Schichten in Gastromatic korrigiert wurden: Die Karte zeigt, was neu ist,
            was schon genau so da ist und was abweicht. Jede Abweichung steht mit bisherigem und
            neuem Stand da; je Zeile <em>Neuer Stand</em> oder <em>Bisherigen behalten</em>.
            Übernehmen geht erst, wenn alles entschieden ist.
          </p>
          <p>
            <strong>Exporte nicht von Hand bearbeiten,</strong> am besten auch nicht in Excel oder
            Numbers speichern: Die schreiben Datumsangaben anders zurück. „1.11.2026“ statt
            „01.11.2026“ liest die App, was sie nicht lesen kann, nennt sie mit Zeile und Spalte.
            Feiertage stehen in Gastromatic
            als „A (Tag der Deutschen Einheit)“; die App zählt sie als Arbeitszeit und nennt sie
            unter „Hinweise beim Lesen“. Eine Stundendatei ohne eine einzige Zeile Arbeitszeit,
            etwa mit geleerter Spalte „Typ“, nimmt sie gar nicht erst an.
          </p>
          <p>
            Nicht in den Import gehören das <em>.xls</em> aus ecos (dort im Exportdialog CSV
            wählen), die Monatsübersicht und die alte Trinkgeld-Mappe. Die App lehnt sie ab und
            sagt, warum.
          </p>
        </Schritte>
        <Warum>
          <p>
            Lesen und Übernehmen sind getrennt, damit sich eine falsche Datei folgenlos
            verwerfen lässt. Die Exporte heißen jeden Monat anders; am Dateinamen ließe sich die
            Quelle nicht sicher erkennen.
          </p>
          <p className="mt-2">
            Dieselbe Datei zweimal einzulesen schadet nicht. Vorhandene Zeilen werden
            übersprungen, nie überschrieben. Was einmal importiert ist, bleibt als Rohwert
            erhalten; <em>Neuer Stand</em> legt eine Korrektur darüber, mit Grund im Audit-Log.
            Verglichen wird je Person, Tag und Bereich, damit eine verschobene Schicht nicht als
            zweite zählt.
          </p>
          <p className="mt-2">
            Ein Tag gehört zu dem Halbmonat, in dem sein Datum liegt, nicht zu der Datei, aus der
            er kam. Ändert ein Import einen freigegebenen Tag, wird der Tag wieder geöffnet und
            muss neu freigegeben werden. Ausgezahlte Tage ändert kein Import mehr; ihr Tagesblatt
            sagt, wenn danach noch Daten eingegangen sind.
          </p>
          <p className="mt-2">
            Taucht eine Personalnummer mit Trinkgeld-Stunden auf, die es in den Stammdaten nicht
            gibt, bricht die Übernahme ab. Ohne Stempeluhr-ID wäre der Aushang unvollständig, und
            das fiele erst ganz am Ende auf.
          </p>
        </Warum>
      </>
    ),
  },
  {
    id: "klaeren",
    titel: "Kassen klären",
    stichworte:
      "review kassenschnitt z-bon trinkgeld differenz kumulativ hinweis empfehlung belege session aufteilen",
    inhalt: (
      <>
        <Schritte>
          <p>
            Jeden Abend melden zwei Geräte dasselbe Geschäft. Die Kasse meldet den Kartenumsatz,
            den sie erwartet (Z-Bon). Das Karten-Terminal meldet, was wirklich angekommen ist
            (Kassenschnitt). Die Differenz ist das Trinkgeld. Die App paart beide Meldungen und
            ordnet sie einem Betriebstag zu. Wo das nicht sicher aufgeht, landet der Fall unter{" "}
            <Maske to="/kassen-review">Kassen klären</Maske>.
          </p>
          <p>Eine aufgeklappte Kasse liest sich von oben nach unten:</p>
          <ol className="ml-5 list-decimal space-y-1">
            <li>
              <strong>Was ist passiert:</strong> der Hergang in Alltagssprache, etwa „am 03.07.
              hat niemand das Karten-Terminal ausgelesen“. Stimmt alles, steht dort nur „✓
              Passt, nichts zu tun“.
            </li>
            <li>
              <strong>Wie die App darauf kommt:</strong> die Rechnung Zeile für Zeile. Hier wird
              auch korrigiert: „ändern“ am Betrag, „eintragen“ an einem fehlenden Wert,
              „festsetzen“ am Trinkgeld.
            </li>
            <li>
              <strong>Zu entscheiden:</strong> nur bei einem roten Hinweis. Höchstens zwei Knöpfe,
              beschriftet mit dem, was man feststellt, etwa „Betrag stimmt“ oder „Aufteilung
              übernehmen“. Der empfohlene steht vorn.
            </li>
            <li>
              <strong>Zählt für:</strong> Bereich und Betriebstag der Kasse, mit „anderer Bereich“
              und „anderer Tag“.
            </li>
          </ol>
          <p>
            Ganz unten lassen sich die Belege aus den Quelldateien aufklappen. Damit lässt sich
            jede Zahl bis in die Ursprungsdatei zurückverfolgen.
          </p>
          <p>
            Zu jeder Entscheidung lässt sich eine Notiz schreiben. Sie ist freiwillig, lohnt sich
            aber, wenn Werte von Hand kommen: Dann steht später dabei, woher der Wert stammt.
          </p>
        </Schritte>
        <Warum>
          <p>
            Beide Geräte zählen kumulativ. Ein Kassenabschluss enthält alles seit dem letzten
            Abschluss, eine Terminal-Auslesung alles seit der letzten Auslesung. Solange jeden
            Abend sauber abgeschlossen wird, passt das. Wird ein Abschluss vergessen und am
            Nachmittag nachgeholt oder ein Terminal erst Tage später ausgelesen, landen zwei
            Abende in einer Zahl. Welcher Abend welchen Teil bekommt, entscheidet, welche Leute
            das Geld bekommen. Das soll ein Mensch bestätigen und nicht still geraten werden.
          </p>
          <p className="mt-2">
            Im Juli 2026 lag die App bei rund neun von zehn Kassentagen auf Anhieb richtig; nur
            die übrigen landen in dieser Liste.
          </p>
        </Warum>
      </>
    ),
  },
  {
    id: "hinweise",
    titel: "Die Hinweise und ihre Farben",
    stichworte:
      "rot gelb grün blockiert kassenschnitt fehlt abschluss fehlt negativ report aufteilen unbekannt nachgeholt verspätet unplausibel gegenwert nebenan übertrag vortag vergessen hohes trinkgeld pfand terminal-id händler-report",
    inhalt: (
      <>
        <Schritte>
          <div className="flex flex-wrap gap-2">
            <span className="chip-alert">rot</span>
            <span className="text-muted">braucht eine Entscheidung und blockiert die Freigabe</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="chip-warn">gelb</span>
            <span className="text-muted">nur ein Hinweis, blockiert nicht</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="chip-ok">✓ grün</span>
            <span className="text-muted">brauchte eine Entscheidung und hat sie bekommen</span>
          </div>
          <p>
            Zum Nachschlagen. Im Alltag schlägt die App an jedem Fall selbst vor, was zu tun
            ist.
          </p>
          <Tabelle
            kopf={["Hinweis", "Bedeutung", "Meist richtig"]}
            zeilen={[
              [
                "Kassenschnitt fehlt",
                "Abschluss ohne Terminal-Auslesung",
                <>
                  <strong>Gegenwert erfassen</strong> mit dem Wert aus dem VR-Händler-Report.
                  Garderobe, Storno, Abendkasse ohne Kartenzahlung:{" "}
                  <strong>Ohne Trinkgeld werten</strong>.
                </>,
              ],
              ["Abschluss fehlt", "Terminal-Auslesung ohne Kassenabschluss", <strong key="a">Ohne Trinkgeld werten</strong>],
              [
                "Trinkgeld negativ",
                "Am Terminal kam weniger an, als die Kasse erwartet",
                <>
                  Immer ein Zuordnungs- oder Datenfehler. Paarung prüfen, dann{" "}
                  <strong>Werte korrigieren</strong>.
                </>,
              ],
              [
                "Report aufteilen / Abschluss aufteilen",
                "Eine Zahl deckt mehrere Abende ab",
                <>
                  <strong>Aufteilung übernehmen</strong>. Kennt jemand die echten Tageswerte:{" "}
                  <strong>Echte Werte eintragen</strong>, für jeden Tag einzeln.
                </>,
              ],
              [
                "Kasse unbekannt",
                "Terminal-ID oder Kassenname fehlt in den Stammdaten",
                <>
                  Kasse unter <Maske to="/stammdaten">Stammdaten</Maske> anlegen, dann{" "}
                  <strong>Neu abgleichen</strong>.
                </>,
              ],
              ["Abschluss nachgeholt", "Abschluss lief tagsüber und wurde dem Vorabend zugeordnet", "Nichts; stimmt der Tag nicht: „anderer Tag“."],
              ["Report verspätet", "Stunden zwischen Abschluss und Auslesung", "Nichts, nur zur Kenntnis."],
              [
                "Trinkgeld unplausibel",
                "Trinkgeld über der eingestellten Schwelle",
                "Nichts, wenn der Betrag zum Abend passt. Sonst stimmt die Paarung nicht: Werte ändern.",
              ],
              ["Gegenwert nebenan", "Mehrere Abschlüsse an einem Abend, eine Auslesung", "Nichts, der Normalfall."],
              [
                "Übertrag",
                "Am Vorabend dieser Kasse wurde ein Gegenwert von Hand erfasst",
                <>
                  <strong>… abziehen</strong>: ein Klick übernimmt den Wert ohne den Übertrag.
                  Fehlte damals nur der Report in der Datei: <strong>Nichts doppelt</strong>.
                </>,
              ],
              [
                "Hohes Trinkgeld",
                "Trinkgeld der Kasse an diesem Abend über der Schwelle (Vorgabe 50 €)",
                "Im Händler-Report prüfen. Stimmt es: „Betrag stimmt“. Fehlte ein Kassenschnitt: „Werte korrigieren“.",
              ],
              ["Umsatz negativ", "Negativer Umsatz an Kasse oder Terminal, etwa reine Pfandrückgabe", <strong key="e">Betrag stimmt</strong>],
            ]}
          />
          <p>
            <strong>Vergessener Kassenschnitt.</strong> Fehlt am Terminal der Schnitt, fehlt am
            ersten Abend der Report, und der Umsatz steckt im nächsten Report derselben Kasse, also
            beim nächsten Einsatz, auch wenn der erst Tage später ist. Oft erkennt die App das
            selbst und teilt den Report auf beide Abende auf. Sonst am ersten Abend den Betrag aus
            dem Händler-Report mit <strong>Gegenwert erfassen</strong> eintragen; die Terminal-ID
            steht unter dem Kassennamen. Die App markiert dann den nächsten Report als „Übertrag“;
            dort zieht ein Klick den Betrag ab, bei mehreren vergessenen Abenden alle zusammen.
          </p>
        </Schritte>
        <Warum>
          Nur was Geld falsch verteilen würde, ist rot. Ein gelber Hinweis weist auf etwas
          Ungewöhnliches hin, das meistens stimmt. Würde auch das blockieren, klickte man es nach
          einer Woche ungelesen weg, und dann auch die roten.
        </Warum>
      </>
    ),
  },
  {
    id: "rechnung",
    titel: "Veranstaltung auf Rechnung",
    stichworte: "event mehrere kassen sammelabschluss mehrfachauswahl ganzen tag wählen trinkgeld gesamt",
    inhalt: (
      <>
        <Schritte>
          <p>
            Läuft ein Event über Rechnung, steht an jeder beteiligten Kasse Umsatz, aber nichts
            am Terminal. Unter <Maske to="/kassen-review">Kassen klären</Maske> lassen sich die
            Kassen links per Kästchen auswählen, mit <strong>ganzen Tag wählen</strong> alle
            eines Abends auf einmal. Oben erscheint dann der Sammelabschluss:
          </p>
          <Tabelle
            kopf={["Aktion", "Wann"]}
            zeilen={[
              [<strong key="1">Ohne Trinkgeld</strong>, "Der Umsatz steht, Trinkgeld gab es keines."],
              [
                <strong key="2">Trinkgeld gesamt</strong>,
                "Ein Betrag für die ganze Veranstaltung. Er wird im Verhältnis der Umsätze auf die Kassen verteilt.",
              ],
              [<strong key="3">So bestätigen</strong>, "Die Werte stimmen, wie sie eingelesen wurden."],
            ]}
          />
          <p>
            Vor dem Übernehmen steht die Aufteilung auf dem Bildschirm. Sie ergibt cent-genau
            den eingegebenen Betrag.
          </p>
        </Schritte>
        <Warum>
          Am 25.08.2026 betraf ein Event zwölf Kassen. Einzeln wäre das zwölfmal dieselbe
          Entscheidung. Eine Vorauswahl gibt es trotzdem nicht: Was durch schnelles Klicken
          bestätigt wird, steht danach als geprüft im Audit-Log.
        </Warum>
      </>
    ),
  },
  {
    id: "gefuehrt-als",
    titel: "Eine Kasse stand woanders",
    stichworte: "heute geführt als bereich topf umwidmen biergarten garderobe",
    inhalt: (
      <>
        <Schritte>
          <p>
            Gehört das Trinkgeld einer Kasse an einem Abend in einen anderen Topf, genügt die
            Spalte <strong>Topf</strong> in der Kassentabelle des Betriebstags: auswählen,
            fertig. Stand das Gerät wirklich an der Stelle einer anderen Kasse, steht an jeder
            aufgeklappten Kasse unten „Zählt für …“ mit <strong>anderer Bereich</strong>: dann
            zählt sie an diesem Abend ganz als die andere Kasse, mit deren Topf. Beides gilt nur
            für diesen einen Abend; die Stammdaten bleiben, wie sie sind. Die Auswahl unter
            „anderer Bereich“ nennt Topf und Terminal-ID jeder Kasse; die eigene Kasse steht
            oben und hebt die Verschiebung wieder auf.
          </p>
          <p>
            Am Betriebstag steht unter der Zielkasse, welche Kasse dort heute lief und mit wie
            viel Trinkgeld. Ein Biergartengerät, das nachts im Club lief, ordnet man einer Kasse
            aus CBE oder Yuca zu; bei „CBE + Yuca zusammen“ ist es gleich, welcher.
          </p>
          <p>
            Am besten gleich eintragen, solange noch jemand weiß, was an dem Abend wo stand.
          </p>
        </Schritte>
        <Warum>
          Das ist der einzige Fall, den die App nicht selbst erkennen kann: Die Beträge sehen
          völlig plausibel aus, nur der Topf stimmt nicht. Im Juli 2026 kam das zweimal vor.
          Wochen später lässt sich so etwas nur noch mühsam rekonstruieren.
        </Warum>
      </>
    ),
  },
  {
    id: "von-hand",
    titel: "Geräte ohne Export und Abende ohne Import",
    stichworte: "wintergarten nur von hand manuell kasse erfassen betriebstag anlegen",
    inhalt: (
      <>
        <Schritte>
          <p>
            Im Wintergarten laufen Terminals einer anderen Firma, die in keinem Export stehen.
            Sie werden einmalig unter <Maske to="/stammdaten">Stammdaten</Maske> als Kasse angelegt, mit Topf,
            ohne Terminal-ID und mit Haken bei <strong>Nur von Hand</strong>. Ist ein Gerät
            gelaufen, am Betriebstag im Kassenblock <strong>Kasse von Hand erfassen</strong>:
            Umsatz und den Teil, der am Terminal bezahlt wurde. Die Differenz ist das Trinkgeld.
          </p>
          <p>
            Hatte an einem Abend nur der Wintergarten offen, liefert kein Import etwas, und der
            Betriebstag existiert noch nicht. Dann unter{" "}
            <Maske to="/betriebstage">Tage freigeben</Maske> → Weitere Optionen →{" "}
            <strong>Betriebstag anlegen</strong>, danach Kassen und Stunden von Hand erfassen.
          </p>
        </Schritte>
        <Warum>
          Eine von Hand erfasste Kasse gilt sofort als bestätigt, weil es keinen Abgleich gibt,
          der etwas vorschlagen könnte. Deshalb lässt auch ein späteres Neu-Abgleichen sie
          stehen. In der Belegliste steht „von Hand erfasst“, erfundene Rohzeilen gibt es nicht.
        </Warum>
      </>
    ),
  },
  {
    id: "rechnen",
    titel: "Rechnen und freigeben",
    stichworte:
      "berechnen freigabe kontrollsumme rundung rundungsausgleich topf gruppe stunden verteilung sammelfreigabe getrennt gemeinsam zusammen teilgebündelt alle in einem topf vorgabe veranstaltung konzert blättern vortag folgetag pfeiltasten sortieren suchen",
    inhalt: (
      <>
        <Schritte>
          <p>
            Sobald an einem Tag jede Kasse geklärt ist, rechnet die App ihn selbst. Wie, steht am
            Betriebstag oben in der Leiste unter <strong>Berechnung</strong>:
          </p>
          <ul className="ml-5 list-disc space-y-1">
            <li>
              <strong>Trinkgeld berechnen (alle in einem Topf):</strong> das Trinkgeld aller
              Kassen geht auf die Stunden aller Bereiche. Der Normalfall.
            </li>
            <li>
              <strong>Trinkgeld getrennt (alle einzeln):</strong> jeder Topf nur auf seine eigenen
              Leute.
            </li>
            <li>
              <strong>Trinkgeld teilgebündelt:</strong> einige Töpfe teilen gemeinsam, die übrigen
              für sich. Etwa ein Konzert im CBE, während im Yuca und im Biergarten Fußball läuft:
              Yuca und Sommergarten ankreuzen, dann teilen die Leute aus Yuca und Biergarten, und
              die im CBE ihr eigenes Trinkgeld.
            </li>
          </ul>
          <p>
            Die Wahl gilt sofort und nur für diesen Tag, der Tag wird neu gerechnet; zurück geht
            es über dieselbe Auswahl. Weicht ein Tag von der Vorgabe ab, ist die Auswahl gelb
            umrandet, und in der Liste trägt er ein Kennzeichen. Die Vorgabe für neue Tage steht
            in den Stammdaten unter Einstellungen.
          </p>
          <p>
            Unter dem Datum lässt sich die <strong>Veranstaltung</strong> eintragen, etwa „CBE:
            Konzert · Yuca und Biergarten: Fußball“. Sie steht dann auch in der Liste der Tage
            und erklärt später, warum ein Tag so gerechnet wurde.
          </p>
          <p>
            Zählt eine einzelne Kasse an einem Abend für einen anderen Topf, stellt man das in der
            Kassentabelle in der Spalte <strong>Topf</strong> um, ebenfalls sofort und nur für
            diesen Tag.
          </p>
          <p>
            Neben dem Datum stehen der vorige und der nächste Betriebstag. Damit, oder mit den
            Pfeiltasten ← und →, geht man die Tage durch, ohne zurück in die Liste.
          </p>
          <p>
            Die Verteilung je Person lässt sich durchsuchen und mit einem Klick auf den
            Spaltenkopf nach Name, Stunden oder Trinkgeld sortieren; ein zweiter Klick dreht die
            Richtung.
          </p>
          <p>
            Unter <Maske to="/betriebstage">Tage freigeben</Maske> steht dann etwa „12 Tage sind
            gerechnet und warten auf Freigabe“, mit einem Knopf für alle. Ein Tag, der liegen
            bleibt, nennt seinen Grund.
          </p>
          <p>
            Oben am Betriebstag muss die Kontrollsumme aufgehen: Summe der Töpfe gleich Summe
            des Verteilten. Sonst bleibt <strong>Freigeben</strong> gesperrt, und der Tooltip am
            Knopf sagt, woran es liegt.
          </p>
          <p>
            Beim Runden entstehen Cent-Differenzen. Sie gehen an den höchsten Einzelanteil und
            sind farbig markiert, wie im alten Tagesblatt. Die Spalte <em>vor Rundung</em> zeigt
            den Anteil mit vier Nachkommastellen, damit jeder Cent nachprüfbar bleibt.
          </p>
        </Schritte>
        <Warum>
          <p>
            Rechnen entscheidet nichts, sechzehn Klicks dafür wären reine Arbeit. Freigeben ist
            dagegen eine Entscheidung und bleibt deshalb beim Menschen. Gesammelt geht es, weil
            die eigentliche Prüfung schon beim Kassenklären passiert ist.
          </p>
          <p className="mt-2">
            Nach dem Freigeben geht es gleich zum nächsten offenen Tag derselben Hälfte.
          </p>
          <p className="mt-2">
            Ein „trotzdem freigeben“ gibt es nicht. Ein Tag mit offenem roten Hinweis oder
            falscher Kontrollsumme würde Geld falsch auszahlen, und das ließe sich nach der
            Überweisung nur noch über Nachbuchungen reparieren.
          </p>
        </Warum>
      </>
    ),
  },
  {
    id: "ohne-empfaenger",
    titel: "Geld in einem Topf ohne Stunden",
    stichworte: "kein empfänger zur kenntnis tagesausgleich sommergarten excel differenz",
    inhalt: (
      <>
        <Schritte>
          <p>
            Läuft eine Kasse, in deren Topf an dem Abend niemand gearbeitet hat, steht am Tag
            ein Hinweis wie: „6,25 € aus dem Topf Sommergarten konnten an niemanden gehen. Der
            Tagesausgleich hat den Betrag an PersNr 853 gegeben.“
          </p>
          <p>
            Das blockiert nicht. Gehört das Geld woanders hin, fehlen entweder Stunden im
            Gastromatic-Export, oder die Kasse zählte an diesem Abend für einen anderen Topf. Dann
            in der Kassentabelle ihren <strong>Topf</strong> umstellen.
          </p>
        </Schritte>
        <Warum>
          Genau so hat das Excel-Tagesblatt immer gerechnet: Die Differenz wurde von Hand in die
          höchste Zeile geschrieben. Die App bildet das nach, damit die Beträge zu dem passen,
          was bisher ausgezahlt wurde, und zeigt es dafür offen an.
        </Warum>
      </>
    ),
  },
  {
    id: "monat",
    titel: "Monatsübersicht und Aushang",
    stichworte: "monatsblatt aushang drucken pdf stempeluhr anonym namen vorschau csv suchen sortieren gitterlinien kopfzeile fußzeile datum adresse",
    inhalt: (
      <>
        <Schritte>
          <p>
            Die <Maske to="/monat">Monatsübersicht</Maske> sieht aus wie das gewohnte
            Monatsblatt: je Person eine Zeile, je Betriebstag eine Spalte. Die beiden Hälften
            sind getrennt, jede mit eigener Summe. Diese Summe wird überwiesen, nicht die
            Monatssumme ganz rechts.
          </p>
          <p>
            Über jeder Hälfte steht ihr Stand: „Vorschau“, solange es keine Auszahlung gibt,
            dann „Auszahlung in Vorbereitung“, „gesperrt, wird ausgezahlt“, „zur Überweisung
            exportiert“ und zuletzt „ausgezahlt“.
          </p>
          <p>
            Über der Tabelle sucht ein Feld nach Name, Personal- oder Stempeluhrnummer. Ein Klick
            auf „Name“, „Summe“ oder „Monat“ sortiert danach, ein zweiter dreht die Richtung,
            etwa vom höchsten Betrag zum niedrigsten.
          </p>
          <p>
            Der <strong>Aushang</strong> ist eine Druckseite. Im Browser{" "}
            <em>Drucken oder als PDF sichern</em>, im Druckdialog Querformat wählen. Die
            Voreinstellung zeigt nur Stempeluhr-IDs; <em>Mit Namen</em> zeigt stattdessen die
            Namen. Aus der Auszahlung heraus zeigt er nur die Hälfte dieser Periode; oben lässt
            sich zwischen erster, zweiter Hälfte und ganzem Monat wechseln. Leere Felder sind ein
            blasser Punkt, jede zweite Zeile ist hinterlegt und feine Linien trennen die Tage,
            damit man beim Kontrollieren nicht verrutscht.
          </p>
          <p>
            Auf Papier fehlen der Gesamtbetrag, die Zahl der Personen und Abende und die Zeile
            mit den Tagessummen: das ist für euch, nicht fürs Brett. Am Bildschirm stehen sie
            weiter. Datum und Adresse am Papierrand lässt Chrome von selbst weg; erscheinen sie
            in einem anderen Browser doch, im Druckdialog unter „Weitere Einstellungen“ das
            Häkchen „Kopf- und Fußzeilen“ entfernen.
          </p>
        </Schritte>
        <Warum>
          Die Fassung mit Stempeluhr-IDs gibt es für Aushänge, an denen Namen nicht stehen
          sollen. Solange Tage offen sind, steht „Vorschau · nicht aushängen“ quer über jeder
          gedruckten Seite. Drucken lässt sich im Browser nicht verhindern, also muss die
          Warnung auf dem Papier stehen.
        </Warum>
      </>
    ),
  },
  {
    id: "auswertung",
    titel: "Durchschnittliches Trinkgeld",
    stichworte: "auswertung durchschnitt schnitt pro stunde pro abend pro monat jahr wie viel bekommt man",
    inhalt: (
      <>
        <Schritte>
          <p>
            Unter <Maske to="/auswertung">Mehr → Auswertung</Maske> steht je Monat und für das
            Jahr, wie viel Trinkgeld es im Schnitt gab:
          </p>
          <ul className="ml-5 list-disc space-y-1">
            <li>
              <strong>pro Stunde</strong> — die beste Antwort auf „Wie viel bekommt man?“, weil
              sie nicht davon abhängt, wie viel jemand arbeitet;
            </li>
            <li>
              <strong>pro Abend und Person</strong> — wer an einem Abend in zwei Bereichen
              stand, zählt einmal;
            </li>
            <li>
              <strong>pro Person im Monat</strong> — hängt stark davon ab, wie oft jemand
              arbeitet.
            </li>
          </ul>
        </Schritte>
        <Warum>
          Gezählt werden nur ausgezahlte Tage, damit sich eine genannte Zahl nicht mehr ändert.
          Für Monate aus der Excel-Übernahme gibt es keine Stunden; dort fehlt „pro Stunde“,
          die anderen Werte stehen.
        </Warum>
      </>
    ),
  },
  {
    id: "auszahlen",
    titel: "Auszahlen",
    stichworte:
      "auszahlungsperiode sperren sepa export iban überweisung bank periode anlegen xml datei sepaapp auftraggeberkonto kontoinhaber ausführungsdatum ausgezahlt doppelt suche",
    inhalt: (
      <>
        <Schritte>
          <p>
            Ist ein Halbmonat vorbei und jeder Tag darin freigegeben, steht unter{" "}
            <Maske to="/auszahlungen">Auszahlen</Maske> „Bereit zur Auszahlung“. Vorgeschlagen
            wird immer der älteste fertige Halbmonat.
          </p>
          <ol className="ml-5 list-decimal space-y-1">
            <li>
              <strong>Auszahlung anlegen.</strong> Name und Zeitraum schlägt die App vor.
            </li>
            <li>
              <strong>Prüfen.</strong> Darunter stehen alle Personen mit Betrag. Fehlt jemandem
              die IBAN, steht dort <em>fehlt, eintragen</em>.
            </li>
            <li>
              <strong>Sperren.</strong> Friert die Beträge ein und sperrt alle Tage der Periode.
            </li>
            <li>
              <strong>Überweisungsdatei herunterladen.</strong> Unter{" "}
              <em>Überweisung an die Bank</em> das Ausführungsdatum wählen (vorgeschlagen ist der
              nächste Bankarbeitstag), dann herunterladen.
            </li>
            <li>
              <strong>Im Online-Banking hochladen</strong> als SEPA-Sammelüberweisung (Datei-Upload).
              Die Bank zeigt vor der Freigabe in der Regel Anzahl und Summe; beides steht auch in der App.
            </li>
            <li>
              <strong>Aushang drucken.</strong>
            </li>
          </ol>
          <p>
            Ältere Perioden findet man über das Feld unter <em>Alle Perioden</em>: aufklappen und
            tippen, etwa „märz“, „09.2026“ oder „ausgezahlt“. Vorausgewählt ist die neueste
            Periode, an der noch etwas zu tun ist.
          </p>
          <p>
            Das Konto, von dem überwiesen wird (Bhf. Ehrenfeld GmbH, IBAN, BIC), trägt die
            Administration einmal unter <em>Überweisung an die Bank → Ändern</em> ein.
          </p>
        </Schritte>
        <Warum>
          <p>
            Eine Periode für einen laufenden Halbmonat oder mit offenen Tagen nimmt die App
            nicht an. Sonst fiele ein Abend still aus der Auszahlung, und das merkte erst die
            Person, die ihr Geld vermisst.
          </p>
          <p className="mt-2">
            Fehlt einer Person die IBAN, gibt es keine Datei. Ein halber Zahllauf ist schlimmer
            als keiner. Deshalb meldet die Übersicht fehlende IBANs schon, sobald jemand
            Trinkgeld bekommt, nicht erst beim Export. Jeder Export steht im Audit-Log.
          </p>
          <p className="mt-2">
            Die Datei ist direkt das Format der Bank (SEPA), wie bisher aus SEPAApp; der Umweg
            über die Excel-Vorlage entfällt. <strong>Jede hochgeladene Datei überweist
            erneut.</strong> Deshalb warnt die Maske, wenn für eine Periode schon eine Datei
            erzeugt wurde, und eine ausgezahlte Periode erzeugt gar keine mehr.
          </p>
          <p className="mt-2">
            Der Name in der Überweisung ist der Name, auf den das Konto läuft, ohne Zusatz wie
            „(CBE)“. Die empfangende Bank gleicht ihn ab. Weicht er ab, unter Stammdaten →
            Mitarbeitende → <em>Bankdaten ändern</em> den <em>Namen am Konto</em> eintragen.
          </p>
        </Warum>
      </>
    ),
  },
  {
    id: "korrigieren",
    titel: "Etwas rückgängig machen",
    stichworte: "zurücknehmen rückgängig import falsche datei wieder öffnen entsperren nachbuchung zurücksetzen fehler löschen",
    inhalt: (
      <>
        <Schritte>
          <p>
            Solange ein Tag nicht freigegeben ist, lässt sich jede bestätigte Kasse darin
            zurücknehmen. Einen freigegebenen Tag kann nur die Administration wieder öffnen,
            und nur mit Grund.
          </p>
          <p>
            Ist der Tag schon in einer gesperrten Auszahlung, kann ihn die Administration mit
            Grund entsperren, solange die Auszahlung noch nicht zur Überweisung exportiert ist.
            Die Auszahlung geht dann mit auf und muss neu gesperrt werden. Ist sie exportiert
            oder ausgezahlt, oder stammt der Tag aus Excel, geht es nicht mehr; eine Differenz
            wird dann von Hand ausgeglichen.
          </p>
          <p>
            <strong>Falsche Datei eingelesen?</strong> Unter <Maske to="/import">Import</Maske>{" "}
            → Frühere Importe → <em>Zurücknehmen</em>. Vorher steht da, welche Zeilen und
            Kassen verschwinden und welche Tage wieder offen sind. Nur wenn die Datei schon in
            einer gesperrten Auszahlung steckt, geht es nicht.
          </p>
          <p>
            War ein ganzer Zeitraum falsch eingelesen, kann die Administration ihn unter{" "}
            <Maske to="/betriebstage">Tage freigeben</Maske> → Weitere Optionen →{" "}
            <strong>Zeitraum zurücksetzen</strong> löschen. Vorher steht genau da, was
            verschwindet, mit Zahlen.
          </p>
        </Schritte>
        <Warum>
          Was gesperrt ist, ist ausgezahlt. Die App rechnet einen gesperrten Tag deshalb nie
          neu, und ein neuer Kassenimport gleicht seine Kassen nicht neu ab, sonst stimmten
          Überweisung und Bestand nicht mehr überein. Vor dem Zurücksetzen
          sichert sie automatisch die ganze Datenbank; schlägt das fehl, löscht sie nichts.
        </Warum>
      </>
    ),
  },
  {
    id: "stammdaten",
    titel: "Stammdaten pflegen",
    stichworte: "kasse terminal getauscht pool topf bereich arbeitsbereich mitarbeiter neu iban konto stempeluhr ausgeschieden einstellungen schwelle vorgabe berechnung",
    inhalt: (
      <>
        <Schritte>
          <p>
            Unter <Maske to="/stammdaten">Mehr → Stammdaten</Maske> liegt alles, was sich
            ändert, ohne dass sich die Software ändert.
          </p>
          <ul className="ml-5 list-disc space-y-1">
            <li>
              <strong>Kassen:</strong> Der Name muss exakt der Spalte „Mitarbeiter“ in den
              ecos-Abschlüssen entsprechen, die Terminal-ID kommt aus dem VR-Export. Wird ein
              Terminal getauscht, hier die neue ID eintragen.
            </li>
            <li>
              <strong>Neuer Bereich mit eigenem Topf:</strong> Pool anlegen, Kassen zuordnen,
              die einzahlen, und Arbeitsbereiche, die daraus bedient werden.
            </li>
            <li>
              <strong>Neue Mitarbeitende:</strong> meldet der Import und
              zeigt dort gleich das Formular: Name und Personalnummer aus Gastromatic sind
              eingetragen, die Stempeluhr-ID vorgeschlagen. IBAN dazu, anlegen, der Import läuft
              weiter.
            </li>
            <li>
              <strong>Mitarbeitende und IBAN:</strong> Oben stehen alle, die Trinkgeld bekommen und
              keine IBAN haben. Dort <em>IBAN eintragen</em>. Unter <em>Bankdaten ändern</em>{" "}
              lässt sich die IBAN ersetzen (nur mit Grund) und der <em>Name am Konto</em>{" "}
              eintragen, falls das Konto anders lautet als der Name in der Liste.{" "}
              <em>Anzeigen</em> zeigt die volle IBAN für 30 Sekunden.
            </li>
            <li>
              <strong>Einstellungen:</strong> oben die <em>Vorgabe für neue Tage</em>, also wie
              ein Tag gerechnet wird, solange niemand etwas anderes wählt (etwa „alle in einem
              Topf“). Sie gilt für Tage, die noch nicht gerechnet sind; schon gerechnete behalten
              ihre Aufteilung. Darunter die Schwellen des Abgleichs, etwa ab wann ein Trinkgeld
              als unplausibel gilt. Das Firmenkonto für die Überweisung steht nicht hier,
              sondern unter <Maske to="/auszahlungen">Auszahlen</Maske>.
            </li>
            <li>
              <strong>Zugänge</strong> (nur Administration): wer die App benutzen darf, mit
              welcher Rolle, und welche Microsoft-Konten auf Zuordnung warten.
            </li>
          </ul>
        </Schritte>
        <Warum>
          <p>
            Kassen und Töpfe werden nie gelöscht, nur deaktiviert. Sonst ließe sich eine alte
            Auszahlung nicht mehr nachvollziehen. Änderungen wirken nur auf künftige
            Berechnungen; bestätigte Kassen und gerechnete Tage bleiben, wie sie sind.
          </p>
          <p className="mt-2">
            Eine Zuordnung, die Geld zwischen Töpfen verschiebt, wird erst vorgemerkt und
            braucht einen zweiten Klick. Ein versehentlicher Klick in ein Auswahlfeld soll kein
            Geld umleiten.
          </p>
        </Warum>
      </>
    ),
  },
  {
    id: "rollen",
    titel: "Wer darf was",
    stichworte: "rolle rechte buchhaltung administrator admin berechtigung benutzer iban bankdaten",
    inhalt: (
      <>
        <Tabelle
          kopf={["Rolle", "Darf"]}
          zeilen={[
            ["Buchhaltung", "Die ganze Abrechnung: importieren, klären, rechnen, freigeben, auszahlen. Sieht IBANs."],
            [
              "Administration",
              "Dazu Stammdaten und Zugänge pflegen, Passwörter zurücksetzen, freigegebene Tage wieder öffnen und Zeiträume zurücksetzen.",
            ],
          ]}
        />
        <Warum>
          Wer abrechnet, soll nicht nebenbei Töpfe umbauen oder ausgezahlte Tage wieder öffnen
          können. Diese Eingriffe verschieben Geld, das schon unterwegs ist. Sie sind deshalb der
          Administration vorbehalten und brauchen immer einen Grund. Geprüft wird auf dem Server bei
          jeder Anfrage; eine geänderte Rolle wirkt sofort.
        </Warum>
      </>
    ),
  },
  {
    id: "anmelden",
    titel: "Anmelden und Konto",
    stichworte:
      "microsoft anmelden outlook konto passwort vergessen einmalpasswort zweiter faktor authenticator code handy gesperrt abgemeldet sitzung iban bankdaten notfallzugang zuordnung",
    inhalt: (
      <>
        <Schritte>
          <p>
            Angemeldet wird mit <strong>Mit Microsoft anmelden</strong>, mit dem Konto, mit dem
            Sie auch Outlook öffnen. Den zweiten Faktor (Code oder Bestätigung auf dem Handy)
            verlangt dabei Microsoft, so wie bei Ihrem Microsoft-Konto eingerichtet; die App
            fragt danach nichts weiter.
          </p>
          <p>
            Beim allerersten Mal erscheint „Wartet auf Zuordnung“. Die Administration ordnet Ihr
            Microsoft-Konto dann unter <Maske to="/stammdaten">Stammdaten</Maske> → Zugänge →{" "}
            <em>Wartet auf Zuordnung</em> einem Zugang zu und legt damit Ihre Rolle fest.
            Danach geht es direkt.
          </p>
        </Schritte>
        <Tabelle
          kopf={["Was passiert", "Was tun"]}
          zeilen={[
            [
              "Microsoft meldet, Sie seien nicht zugewiesen",
              "Ihr Konto ist in Microsoft noch nicht für die App freigeschaltet. Das macht die IT.",
            ],
            [
              "Plötzlich wieder bei der Anmeldung",
              "Nach zwei Stunden ohne Klick endet die Sitzung, nach zwölf Stunden in jedem Fall.",
            ],
            [
              "Anmelden mit Anmeldename und Passwort",
              "Nur für den Notfallzugang der Administration, falls Microsoft nicht erreichbar ist. Dazu gehört ein Code aus der Authenticator-App.",
            ],
            [
              "Notfallzugang: Passwort vergessen oder gesperrt",
              "Nach fünf Fehlversuchen ist er eine Viertelstunde gesperrt. Ein zweiter Zugang mit Administrationsrechten kann das Passwort zurücksetzen oder die Sperre aufheben.",
            ],
            [
              "Notfallzugang: Handy verloren",
              "Ein zweiter Zugang mit Administrationsrechten setzt den zweiten Faktor zurück; beim nächsten Anmelden wird er neu eingerichtet.",
            ],
          ]}
        />
        <Warum>
          <p>
            Wer die App benutzt, entscheidet zweimal jemand: die IT, die das Microsoft-Konto für
            die App freischaltet, und die Administration, die es einem Zugang zuordnet. Eine
            passende E-Mail-Adresse allein reicht nie, denn Adressen werden neu vergeben.
          </p>
          <p className="mt-2">
            Ein Passwort nur in der App gibt es bewusst nicht mehr für alle: Wer das Unternehmen
            verlässt, wird bei Microsoft gesperrt und kommt damit auch hier nicht mehr hinein.
          </p>
        </Warum>
      </>
    ),
  },
  {
    id: "fragen",
    titel: "Häufige Fragen",
    stichworte: "warum betrag fehlt jemand excel abweichung audit nachvollziehen",
    inhalt: (
      <Schritte>
        <p>
          <strong>Woher kommt dieser Betrag?</strong>
          <br />
          An der Kasse die Belege aufklappen: Sie zeigen die Rohzeilen aus den Quelldateien.
          Spätere Änderungen stehen unter <Maske to="/audit">Mehr → Audit</Maske>, mit Vorher,
          Nachher und wer es war.
        </p>
        <p>
          <strong>Warum lässt sich der Tag nicht freigeben?</strong>
          <br />
          Mit der Maus auf den grauen Knopf <em>Freigeben</em> zeigen, der Tooltip nennt den
          Grund. Meist ist noch eine Kasse offen oder die Kontrollsumme geht nicht auf.
        </p>
        <p>
          <strong>Warum fehlt jemand in der Auszahlung?</strong>
          <br />
          Entweder hat die Person im Zeitraum keine Stunden in einem trinkgeldberechtigten
          Bereich, oder ihr Tag ist noch nicht freigegeben. Die Monatsübersicht zeigt offene
          Tage eigens an.
        </p>
        <p>
          <strong>Warum ist bis Ende August schon alles ausgezahlt?</strong>
          <br />
          Bis Ende August wurde in Excel abgerechnet und über SEPAApp überwiesen.
          Diese Beträge hat die App je Person und Tag aus Excel übernommen, gesperrt und als
          ausgezahlt vermerkt. Sie lassen sich ansehen und für den Aushang drucken, werden aber
          nie neu gerechnet. Kassen und Stunden gibt es dazu nicht, weil Excel sie nicht führt.
        </p>
        <p>
          <strong>Ich habe eine Überweisungsdatei zweimal erzeugt.</strong>
          <br />
          Nur eine davon hochladen. Jede Datei überweist den vollen Betrag. Erzeugt die App eine
          zweite, warnt sie vorher; im Audit-Log steht jede mit Uhrzeit.
        </p>
        <p>
          <strong>Die Zahlen weichen vom alten Excel ab.</strong>
          <br />
          Zwei Unterschiede sind bekannt und gewollt. Arbeitet jemand an einem Tag in mehreren
          Bereichen, zählt die App alle Zeilen; die alte Excel-Formel übersah dabei die
          mittleren. Und der Rundungsausgleich kann an einzelnen Tagen um einen Cent anders
          fallen.
        </p>
      </Schritte>
    ),
  },
  {
    id: "grundsaetze",
    titel: "Was die App bewusst nicht tut",
    stichworte: "grundsätze regeln überschreiben raten bargeld",
    inhalt: (
      <ul className="ml-5 list-disc space-y-2 text-sm leading-relaxed">
        <li>
          <strong>Sie überschreibt keine importierte Zeile.</strong> Korrekturen liegen darüber,
          der Rohwert bleibt. So lässt sich jede Änderung zurückverfolgen; in Excel verschwand
          eine überschriebene Zelle spurlos.
        </li>
        <li>
          <strong>Sie gibt nie selbst frei.</strong> Rechnen ja, freigeben nein.
        </li>
        <li>
          <strong>Sie rechnet keinen gesperrten Tag neu,</strong> weder den Altbestand aus Excel
          noch eine ausgezahlte Periode.
        </li>
        <li>
          <strong>Sie rät nicht.</strong> Wo sie nicht sicher entscheiden kann, fragt sie.
        </li>
        <li>
          <strong>Sie kennt kein Bartrinkgeld.</strong> Der Betrieb ist bargeldlos.
        </li>
      </ul>
    ),
  },
];

function passt(t: Thema, suche: string): boolean {
  const q = suche.trim().toLowerCase();
  if (!q) return true;
  const heu = `${t.titel} ${t.stichworte}`.toLowerCase();
  return q.split(/\s+/).every((w) => heu.includes(w));
}

export function Hilfe() {
  const [suche, setSuche] = useState("");
  const sichtbar = THEMEN.filter((t) => passt(t, suche));

  // Ein Link wie /hilfe#hinweise soll auf dem Thema landen. Der Browser springt
  // vor dem ersten Zeichnen und findet das Ziel dann noch nicht.
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [hash]);

  return (
    <>
      <PageHeader label="Hilfe" title="Wie die App arbeitet">
        Was in welcher Maske zu tun ist und warum die App es so macht. Die Themen folgen dem
        Arbeitsablauf. Von jeder Seite aus führt <strong>? Hilfe</strong> oben rechts zum
        passenden Thema, und an vielen Stellen erklärt ein <strong>?</strong> direkt in der
        Maske, was eine Zahl bedeutet.
      </PageHeader>

      <div className="grid gap-10 lg:grid-cols-[14rem_1fr]">
        <nav aria-label="Themen" className="lg:sticky lg:top-32 lg:self-start">
          <input
            type="search"
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            placeholder="Suchen, z. B. IBAN"
            className="field mb-4 w-full"
            aria-label="Hilfe durchsuchen"
          />
          <ul className="space-y-1 text-sm">
            {sichtbar.map((t) => (
              <li key={t.id}>
                <a href={`#${t.id}`} className="text-muted hover:text-ink">
                  {t.titel}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="min-w-0 max-w-3xl">
          {sichtbar.length === 0 ? (
            <p className="text-sm text-muted">
              Nichts gefunden. Ein anderes Wort versuchen, etwa „Kasse“ oder „Auszahlung“.
            </p>
          ) : null}
          {sichtbar.map((t) => (
            <section key={t.id} id={t.id} className="mb-12 scroll-mt-32">
              <h2 className="mb-3 text-xl">{t.titel}</h2>
              {t.inhalt}
            </section>
          ))}
        </div>
      </div>
    </>
  );
}
